/**
 * markdownOutline — a rendered document's headings as a table of contents, and the same HTML with an
 * address on each heading so the contents can link to it.
 *
 * Pure: it takes the HTML a Markdown renderer has already made and sanitised (markdown-it, then
 * DOMPurify, as renderChatMarkdown does) and returns a string and a list, so it loads under
 * `node --test` and any surface that draws a document can share it — a site's item page today, the
 * Guide next. It reads the HTML rather than the Markdown so the outline is exactly the headings that
 * were drawn: a `#` inside a code block or a table is not a heading, and the renderer already knows.
 *
 * ADDRESSES. A heading's id is its words as a slug ("6.13 Shared Abundance" → `6-13-shared-abundance`),
 * the second of the same words taking `-2`. The words are the address because they survive an edit
 * elsewhere in the document, where a position ("the twelfth heading") would not. Only `[a-z0-9-]`
 * reaches the attribute, so the id is added after sanitising without opening anything up.
 */

const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' };

/** A fragment of HTML as the words a reader sees. */
export function htmlText(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&(#\d+|#x[0-9a-f]+|[a-z]+\d*);/gi, (m, e) => {
      if (ENTITIES[e.toLowerCase()] != null) return ENTITIES[e.toLowerCase()];
      if (/^#x/i.test(e)) return String.fromCodePoint(parseInt(e.slice(2), 16));
      if (/^#/.test(e)) return String.fromCodePoint(Number(e.slice(1)));
      return m;
    })
    .replace(/\s+/g, ' ')
    .trim();
}

/** Words as an address: lower case, accents dropped, anything else between words a single hyphen. */
export function headingSlug(text) {
  return String(text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80).replace(/-+$/, '') || 'section';
}

/**
 * The headings of `html` from `minLevel` to `maxLevel`, each given an id, and the HTML carrying them.
 * A heading that already has attributes keeps its own id if it has one.
 *
 * @returns {{ html: string, outline: Array<{ level: number, text: string, id: string }> }}
 */
export function outlineHtml(html, { minLevel = 2, maxLevel = 4 } = {}) {
  const taken = new Map();
  const outline = [];
  const out = String(html || '').replace(/<h([1-6])(\s[^>]*)?>([\s\S]*?)<\/h\1>/g, (m, lvl, attrs = '', inner) => {
    const level = Number(lvl);
    const text = htmlText(inner);
    if (level < minLevel || level > maxLevel || !text) return m;
    const own = /\sid="([^"]+)"/.exec(attrs)?.[1];
    let id = own;
    if (!id) {
      const base = headingSlug(text);
      const n = (taken.get(base) || 0) + 1;
      taken.set(base, n);
      id = n === 1 ? base : `${base}-${n}`;
    }
    outline.push({ level, text, id });
    return own ? m : `<h${level}${attrs} id="${id}">${inner}</h${level}>`;
  });
  return { html: out, outline };
}

/**
 * The outline as a tree: each heading of the top level present, with the next level down under it.
 * Deeper headings are left out — a contents list is for finding your place, not for reading.
 *
 * @returns {Array<{ text: string, id: string, children: Array<{ text: string, id: string }> }>}
 */
export function outlineTree(outline) {
  if (!outline?.length) return [];
  const top = Math.min(...outline.map((h) => h.level));
  const tree = [];
  for (const h of outline) {
    if (h.level === top) tree.push({ text: h.text, id: h.id, children: [] });
    else if (h.level === top + 1) {
      if (!tree.length) tree.push({ text: '', id: null, children: [] });
      tree[tree.length - 1].children.push({ text: h.text, id: h.id });
    }
  }
  return tree;
}

/** Every Markdown table wrapped in a box of its own, so a wide one scrolls sideways inside it rather
 *  than pushing the page wider than the screen. */
export function wrapTables(html, className) {
  return String(html || '').replace(/<table>/g, `<div class="${className}"><table>`).replace(/<\/table>/g, '</table></div>');
}
